/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useImperativeHandle,
  useState
} from 'react';
import {Typography} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import styles from '../../editors/editorStyles';
import useExtendStyles from '../../hooks/useExtendStyles';
import UserNotificationDialog from '../../notifications/UserNotificationDialog';
import ConditionalRender from '../../render/ConditionalRender';
import EmbeddedForm from './EmbeddedForm';

interface CancelFromAllJobsFormProps {
  onSave: (details: GenericObject) => void;
};

const useStyles = () => ({
  formTemplate : {
    marginTop : '20px'
  }
});

/**
 * Re-usable form with the details for cancel from all jobs entity.
*/
export const CancelFromAllJobsForm = forwardRef(({
  onSave
}: CancelFromAllJobsFormProps, ref) => {
  const dialogStyles                              = useExtendStyles(styles, useStyles);
  const classes                                   = dialogStyles();
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  const {
    label,
    cancelMessage,
    selectMessage,
    warningMessage
  } = translate('common:editors.bulkActions.cancelFromAllJobs', {returnObjects: true});

  const {
    yesContinue,
    neverMind
  } = translate('common:editors.shared', {returnObjects: true});

  const onSubmit = () => {
    setShowWarningDialog(true);
  };

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  const onProceed = function() {
    onSave({});
    setShowWarningDialog(false);
  };

  const onDismiss = function() {
    setShowWarningDialog(false);
  };

  return (
    <EmbeddedForm>
      <Typography component='h1' variant='h2'>
        {label}
      </Typography>
      <div className={classes.formTemplate}>
        {selectMessage}
      </div>
      <div className={classes.formTemplate}>
        {cancelMessage}
      </div>
      <ConditionalRender condition={showWarningDialog}>
        <UserNotificationDialog show
          capitalize
          onAction={onProceed}
          onDismiss={onDismiss}
          title={label}
          message={warningMessage}
          dismissLabel={neverMind}
          actionLabel={yesContinue} />
      </ConditionalRender>
    </EmbeddedForm>
  );
});

export default memo(CancelFromAllJobsForm);
